import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Alert,
  AlertIcon,
  Box,
  Button,
  IconButton,
  HStack,
  VStack,
  SimpleGrid,
  Text,
  Checkbox,
  Badge,
  Select,
  Input,
  Skeleton,
  useToast,
} from "@chakra-ui/react";
import { CurrencyText, EmptyState } from "../../../shared/ui";
import { Calendar, ChevronLeft, ChevronRight } from "../../../shared/icons";
import { adicionarMeses, formatarMesAno } from "../../../shared/lib/date";
import { isPlanoBloqueado } from "../../../shared/api/planoBloqueado";
import { apiErrorMessage } from "../../../shared/api/apiErrorMessage";
import { formatScheduleDate } from "../../shiftPatterns/utils/describeShiftPattern";
import { useVinculosQuery } from "../../vinculos/hooks/useVinculosQuery";
import { suggestedShiftValue } from "../../vinculos/utils/suggestedShiftValue";
import { useGoogleEventsQuery, useImportGoogleShiftsMutation } from "../hooks/googleImportQueries";
import { describeImportResult } from "../utils/importSummary";

// Rótulo curto do evento, usado nos nomes acessíveis dos campos da linha.
const eventLabel = (event) => `${formatScheduleDate(event.data)} ${event.titulo || "Sem título"}`;

// Horas do evento quando ele tem hora; no dia inteiro, as horas de referência do vínculo.
const defaultHours = (event, vinculo) => event.horas ?? vinculo?.horas_plantao ?? "";

function initialRow(event, vinculosById) {
  const vinculo = vinculosById.get(event.vinculo_sugerido_id);
  return {
    selected: !event.ja_importado && Boolean(vinculo),
    vinculoId: vinculo ? String(vinculo.id) : "",
    horas: defaultHours(event, vinculo),
    valor: suggestedShiftValue(vinculo, event.data),
  };
}

const isRowComplete = (row) => row.vinculoId !== "" && Number(row.horas) > 0 && Number(row.valor) > 0;

// Lista os eventos do Google Agenda de um mês para virarem plantões. Os que têm o nome de
// um vínculo no título já vêm marcados com ele; os já importados aparecem só para
// conferência. Importar nunca mexe no evento do Google.
export default function ImportGoogleShiftsModal({ isOpen, onClose, initialYear, initialMonth }) {
  const toast = useToast();
  const [period, setPeriod] = useState({ year: initialYear, month: initialMonth });
  useEffect(() => {
    if (isOpen) setPeriod({ year: initialYear, month: initialMonth });
  }, [isOpen, initialYear, initialMonth]);

  const { data: vinculosData } = useVinculosQuery();
  const vinculos = useMemo(() => vinculosData ?? [], [vinculosData]);
  const vinculosById = useMemo(() => new Map(vinculos.map((v) => [v.id, v])), [vinculos]);

  const { data, isLoading, error } = useGoogleEventsQuery(period, { enabled: isOpen });
  const events = useMemo(() => data ?? [], [data]);

  const [rows, setRows] = useState({});
  useEffect(() => {
    setRows(Object.fromEntries(events.map((event) => [event.id, initialRow(event, vinculosById)])));
  }, [events, vinculosById]);

  const updateRow = (id, patch) => setRows((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  const changeVinculo = (event, vinculoId) => {
    const vinculo = vinculosById.get(Number(vinculoId));
    updateRow(event.id, { vinculoId, horas: defaultHours(event, vinculo), valor: suggestedShiftValue(vinculo, event.data) });
  };

  const chosen = events.filter((event) => !event.ja_importado && rows[event.id]?.selected);
  const incomplete = chosen.some((event) => !isRowComplete(rows[event.id]));
  const missingVinculo = chosen.some((event) => rows[event.id].vinculoId === "");
  const total = chosen.reduce((acc, event) => acc + (Number(rows[event.id].valor) || 0), 0);
  const mutation = useImportGoogleShiftsMutation();

  const moveMonth = (delta) =>
    setPeriod((p) => {
      const { ano, mes } = adicionarMeses(p.year, p.month, delta);
      return { year: ano, month: mes };
    });

  const submit = () => {
    const itens = chosen.map((event) => {
      const row = rows[event.id];
      const vinculo = vinculosById.get(Number(row.vinculoId));
      return {
        google_event_id: event.id,
        vinculo_id: Number(row.vinculoId),
        data: event.data,
        hora_inicio: event.hora_inicio ?? vinculo?.hora_inicio_padrao ?? "",
        horas: Number(row.horas),
        valor: Number(row.valor),
      };
    });
    mutation.mutate(itens, {
      onSuccess: (result) => {
        toast({ title: "Importação concluída", description: describeImportResult(result ?? {}), status: "success", duration: 6000 });
        onClose();
      },
      onError: (err) => {
        if (isPlanoBloqueado(err)) return;
        toast({
          title: "Não foi possível importar",
          description: apiErrorMessage(err),
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      },
    });
  };

  let body;
  if (isLoading) {
    body = <Skeleton height="200px" borderRadius="md" />;
  } else if (error) {
    body = (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        {apiErrorMessage(error) ?? "Não foi possível ler o Google Agenda agora. Tente de novo em instantes."}
      </Alert>
    );
  } else if (events.length === 0) {
    body = (
      <EmptyState
        icon={Calendar}
        title="Nenhum evento neste mês"
        description="Os eventos da sua agenda principal do Google aparecem aqui. Os que o próprio MedStation criou ficam de fora."
      />
    );
  } else {
    body = (
      <VStack align="stretch" spacing={0}>
        {events.map((event) => {
          const row = rows[event.id];
          if (!row) return null;
          const label = eventLabel(event);
          const when = event.dia_inteiro ? "Dia inteiro" : event.hora_inicio;
          return (
            <Box key={event.id} py={2.5} borderBottom="1px solid" borderColor="app-border">
              <HStack justify="space-between" align="start" spacing={3}>
                <Checkbox
                  isChecked={!event.ja_importado && row.selected}
                  isDisabled={event.ja_importado}
                  onChange={() => updateRow(event.id, { selected: !row.selected })}
                >
                  <Text fontSize="sm">
                    <Text as="span" fontWeight="medium">
                      {formatScheduleDate(event.data)} · {when}
                    </Text>{" "}
                    · {event.titulo || "Sem título"}
                  </Text>
                </Checkbox>
                {event.ja_importado ? (
                  <Badge variant="statusSubtle" colorScheme="gray" flexShrink={0}>
                    Já importado
                  </Badge>
                ) : null}
              </HStack>
              {event.ja_importado ? null : (
                <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2} mt={2} pl={{ base: 0, sm: 6 }}>
                  <Select
                    size="sm"
                    aria-label={`Vínculo de ${label}`}
                    placeholder="Escolha o vínculo"
                    value={row.vinculoId}
                    onChange={(e) => changeVinculo(event, e.target.value)}
                  >
                    {vinculos.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.nome}
                      </option>
                    ))}
                  </Select>
                  <Input
                    size="sm"
                    type="number"
                    min={0}
                    step="0.5"
                    aria-label={`Horas de ${label}`}
                    placeholder="Horas"
                    value={row.horas}
                    onChange={(e) => updateRow(event.id, { horas: e.target.value })}
                  />
                  <Input
                    size="sm"
                    type="number"
                    min={0}
                    step="0.01"
                    aria-label={`Valor de ${label}`}
                    placeholder="Valor (R$)"
                    value={row.valor}
                    onChange={(e) => updateRow(event.id, { valor: e.target.value })}
                  />
                </SimpleGrid>
              )}
            </Box>
          );
        })}
      </VStack>
    );
  }

  const buttonLabel = chosen.length === 1 ? "Importar 1 plantão" : `Importar ${chosen.length} plantões`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Importar do Google Agenda</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <HStack justify="space-between" mb={4}>
            <IconButton aria-label="Mês anterior" icon={<ChevronLeft size={18} />} variant="ghost" size="sm" onClick={() => moveMonth(-1)} />
            <Text fontWeight="semibold">{formatarMesAno(period.year, period.month)}</Text>
            <IconButton aria-label="Próximo mês" icon={<ChevronRight size={18} />} variant="ghost" size="sm" onClick={() => moveMonth(1)} />
          </HStack>
          {body}
        </ModalBody>
        <ModalFooter gap={3} justifyContent="space-between" flexWrap="wrap">
          <Text fontSize="sm" color={incomplete ? "warning-text-strong" : "app-muted-fg"}>
            {incomplete ? (
              missingVinculo ? "Escolha o vínculo dos eventos marcados." : "Informe horas e valor dos eventos marcados."
            ) : chosen.length > 0 ? (
              <>
                {chosen.length} plantão(ões) · <CurrencyText value={total} />
              </>
            ) : null}
          </Text>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button onClick={submit} isDisabled={chosen.length === 0 || incomplete} isLoading={mutation.isPending}>
              {buttonLabel}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
